import styled from 'styled-components'
import React, { Suspense } from 'react'
import { Link } from 'react-router-dom'
import Button from 'components/ui/button'
import FadeIn from 'components/ui/fadeIn'
import { Copy } from '@stokr/components-library'
import BaseLayout from 'components/layouts/baseLayout/BaseLayout'
import { isChunkLoadError, reloadForStaleChunk } from 'lib/staleChunkReload'

const Main = styled.div`
  align-items: center;
  color: rgba(0, 0, 0, 0.87);
  display: flex;
  flex-direction: column;
  font-family: 'Open Sans', sans-serif;
  min-height: 60vh;
  justify-content: center;
  padding: 20px;
  text-align: center;
  width: 100%;
`

const Title = styled.h1`
  font-size: 48px;
  font-weight: 800;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: #ee220d;
  margin: 0 0 1rem;

  @media screen and (max-width: 991px) {
    font-size: 28px;
  }
`

const Message = styled.p`
  max-width: 480px;
  margin: 0 0 2rem;
  line-height: 1.5;
`

const Actions = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  justify-content: center;
`

export class FallbackErrorBoundary extends React.Component {
  constructor(props) {
    super(props)
    this.state = { error: null }
  }

  static getDerivedStateFromError(error) {
    // Render nothing while componentDidCatch reloads, so the error screen doesn't flash.
    return { error, reloading: isChunkLoadError(error) }
  }

  componentDidCatch(error) {
    console.error('[Fallback] lazy load failed:', error)
    if (this.state.reloading && !reloadForStaleChunk()) {
      this.setState({ reloading: false })
    }
  }

  render() {
    if (this.state.reloading) return null
    if (this.state.error) {
      return (
        <BaseLayout notFullHeight>
          <FadeIn>
            <Main>
              <Title>
                <Copy id="errorBoundary.title" />
              </Title>
              <Message>
                <Copy id="errorBoundary.messagePrefix" /> <a href="mailto:support@stokr.io">support@stokr.io</a>
                <Copy id="errorBoundary.messageSuffix" />
              </Message>
              <Actions>
                <Button color="transparent" onClick={() => window.location.reload()}>
                  <Copy id="errorBoundary.reloadPage" />
                </Button>
                <Link style={{ textDecoration: 'none' }} to="/">
                  <Button>
                    <Copy id="common.goToHomepage" />
                  </Button>
                </Link>
              </Actions>
            </Main>
          </FadeIn>
        </BaseLayout>
      )
    }
    return this.props.children
  }
}
// Default Suspense fallback is null on purpose. With SSR + React.lazy, hydration
// can briefly hit a Suspense boundary before the route's chunk loads, causing
// a full-screen StokrLoader to flash and replace already-rendered content.
// `null` means: keep what's there until the chunk arrives. For client-side
// navigation, React Router keeps the previous page visible during loads
// (startTransition is the default), so no fallback is needed there either.
//
// Pass `fallback` for Module Federation remotes: those have no SSR HTML, so
// without a loader the user sees a blank screen while remoteEntry.js loads.
export const Fallback = ({ children, fallback = null }) => {
  return (
    <FallbackErrorBoundary>
      <Suspense fallback={fallback}>{children}</Suspense>
    </FallbackErrorBoundary>
  )
}
